import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Unlock, Search } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { StatCard, Panel, EmptyState } from "../Common";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const REASONS = { wrong_password: "Wrong password", unknown_email: "Unknown email", inactive: "Account deactivated", locked: "Blocked while locked" };

const fmtDT = (s) => {
  if (!s) return "—";
  const d = new Date(s.endsWith("Z") || s.includes("+") ? s : `${s}Z`);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const browser = (ua) => {
  if (!ua) return "—";
  const b = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Other";
  const os = /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${b} · ${os}` : b;
};

export const LoginActivityTab = () => {
  const [data, setData] = useState(null);
  const [outcome, setOutcome] = useState("all");
  const [q, setQ] = useState("");

  const load = () => api.get("/security/login-activity", { params: { ...(outcome !== "all" && { outcome }), ...(q && { q }) } }).then((r) => setData(r.data));
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [outcome, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const unlock = async (l) => {
    try { await api.delete(`/security/locks/${l.id}`); toast.success(`${l.email} unlocked`); load(); } catch (err) { toast.error(formatErr(err)); }
  };

  if (!data) return <div className="text-sm text-[#6E6E73]">Loading...</div>;
  return (
    <div className="space-y-6" data-testid="login-activity-tab">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Sign-ins (24h)" value={data.stats.success_24h} testid="stat-signins-24h" />
        <StatCard label="Failed attempts (24h)" value={data.stats.failed_24h} testid="stat-failed-24h" />
        <StatCard label="Locked right now" value={data.stats.locked} testid="stat-locked" accent />
      </div>
      <Panel title="Locked accounts" testid="locked-accounts-panel">
        <p className="text-xs text-[#6E6E73] -mt-2 mb-3">After 5 failed attempts, sign-in is blocked for 15 minutes for that email from that network.</p>
        {!data.locked.length ? <EmptyState text="No accounts are locked" testid="locked-empty" /> : (
          <div className="divide-y divide-[#F0F0F2]">
            {data.locked.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-3 gap-3" data-testid={`locked-row-${l.id}`}>
                <div className="min-w-0"><div className="font-semibold truncate">{l.email}</div><div className="text-xs text-[#6E6E73]">{l.count} failed · IP {l.ip || "—"} · unlocks {fmtDT(l.unlocks_at)}</div></div>
                <Button size="sm" variant="outline" onClick={() => unlock(l)} data-testid={`unlock-${l.id}`}><Unlock className="h-4 w-4 mr-1" />Unlock</Button>
              </div>
            ))}
          </div>
        )}
      </Panel>
      <Panel title="Recent sign-in activity" testid="login-events-panel">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Tabs value={outcome} onValueChange={setOutcome}>
            <TabsList>{[["all", "All"], ["success", "Successful"], ["failed", "Failed"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`login-filter-${v}`}>{l}</TabsTrigger>)}</TabsList>
          </Tabs>
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
            <Input placeholder="Search email" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="login-search-input" />
          </div>
        </div>
        {!data.events.length ? <EmptyState text="No sign-in activity yet" testid="login-events-empty" /> : (
          <div className="overflow-x-auto">
            <Table data-testid="login-events-table">
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Email</TableHead><TableHead>Result</TableHead><TableHead>IP</TableHead><TableHead>Device</TableHead></TableRow></TableHeader>
              <TableBody>{data.events.map((e) => (
                <TableRow key={e.id} data-testid={`login-event-${e.id}`}>
                  <TableCell className="whitespace-nowrap">{fmtDT(e.created_at)}</TableCell>
                  <TableCell><div className="font-semibold">{e.email}</div>{e.user_name && <div className="text-xs text-[#6E6E73]">{e.user_name}</div>}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${e.success ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                      {e.success ? "Signed in" : REASONS[e.reason] || "Failed"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{e.ip || "—"}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{browser(e.user_agent)}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        )}
        <p className="text-[11px] text-[#6E6E73] mt-3">Showing the latest 200 events. Activity is kept for 90 days.</p>
      </Panel>
    </div>
  );
};
